(() => {
  function initThreeMvpsSection(section) {
    if (!section || section.dataset.threeMvpsInitialized === 'true') return;

    section.dataset.threeMvpsInitialized = 'true';

    const desktopTrack = section.querySelector('[data-mvp-track]');
    const mobileTrack = section.querySelector('[data-mvp-mobile-track]');
    const tabs = Array.from(section.querySelectorAll('[data-mvp-tab]'));
    const desktopPanels = Array.from(section.querySelectorAll('[data-mvp-panel]'));
    const mobilePanels = Array.from(section.querySelectorAll('[data-mvp-mobile-panel]'));
    const mobileCurrentNumber = section.querySelector('[data-mvp-current-number]');
    const mobileCurrentTitle = section.querySelector('[data-mvp-current-title]');
    const mobileTitleWrap = section.querySelector('.three-mvps-mobile-title');

    let desktopActiveIndex = -1;
    let mobileActiveIndex = -1;
    let ticking = false;

    function getIsMobile() {
      return window.matchMedia('(max-width: 991px)').matches;
    }

    function preloadImages() {
      const images = Array.from(section.querySelectorAll('.three-mvps-image'));

      images.forEach((image) => {
        image.decoding = 'async';

        if (image.complete) return;

        if (typeof image.decode === 'function') {
          image.decode().catch(() => {});
        }
      });
    }

    /*
      Desktop:
      2nd layer top = 40px
      3rd layer top = 80px

      Mobile:
      2nd layer top = 25px
      3rd layer top = 50px
    */
    const DESKTOP_STACK_PEEK = 40;
    const MOBILE_STACK_PEEK = 25;

    /*
      Mobile title safe gap:
      Cards title ke niche se start honge, overlap nahi hoga.
    */
    const MOBILE_TITLE_EXTRA_GAP = 16;

    const DESKTOP_LAYER_GAP = 40;
    const MOBILE_LAYER_GAP = 15;

    const DESKTOP_SCALE_MIN = 0.75;
    const MOBILE_SCALE_MIN = 0.82;

    /*
      Lead hold: the first card stays fully in view (text + button readable) for
      this much extra scroll before the stack starts collapsing. Expressed as a
      fraction of the viewport; keep in sync with --mvp-mobile-lead in the CSS
      (60svh ≈ 0.6 viewport) so the added track height matches this offset.
    */
    const MOBILE_LEAD_RATIO = 0.6;

    /*
      Shorter dwell applied to every intermediate card (not the first, which uses
      the longer lead above, and not the last). Weight is relative to one
      card-to-card transition (1.0); keep in sync with --mvp-mobile-rest-hold in
      the CSS so the added track height matches.
    */
    const MOBILE_REST_HOLD_RATIO = 0.3;

    function protectMobileTitle() {
      if (!mobileTitleWrap) return;

      mobileTitleWrap.style.position = 'relative';
      mobileTitleWrap.style.zIndex = '50';
      mobileTitleWrap.style.pointerEvents = 'none';
    }

    function getMobileTitleOffset() {
      if (!mobileTitleWrap || !getIsMobile()) return 0;

      const titleHeight = mobileTitleWrap.offsetHeight || mobileTitleWrap.getBoundingClientRect().height || 0;

      return titleHeight + MOBILE_TITLE_EXTRA_GAP;
    }

    function getLayerScale(panel, gap, depth, minScale) {
      const width = panel.offsetWidth || panel.getBoundingClientRect().width;

      if (!width) return 1;

      const sideGap = gap * depth;
      const targetScale = (width - sideGap * 2) / width;

      return Math.max(minScale, Math.min(targetScale, 1));
    }

    function applyDesktopStack(stackPos) {
      const total = desktopPanels.length;
      if (!total) return;

      desktopPanels.forEach((panel, i) => {
        const rel = i - stackPos;

        let translate = '0px';
        let scale = 1;
        let opacity = 1;

        if (rel >= 1) {
          translate = '110%';
          opacity = 0;
        } else if (rel > 0) {
          translate = rel * 110 + '%';
        } else {
          const depth = -rel;

          translate = -(depth * DESKTOP_STACK_PEEK) + 'px';

          if (depth > 0) {
            scale = getLayerScale(panel, DESKTOP_LAYER_GAP, depth, DESKTOP_SCALE_MIN);
          }
        }

        panel.style.zIndex = String(i + 1);
        panel.style.opacity = String(opacity);
        panel.style.transform = 'translate3d(0, ' + translate + ', 0) scale(' + scale + ')';
      });

      const safeIndex = Math.max(0, Math.min(Math.round(stackPos), total - 1));

      if (safeIndex !== desktopActiveIndex) {
        desktopActiveIndex = safeIndex;

        tabs.forEach((tab, i) => {
          tab.classList.toggle('is-active', i === safeIndex);
        });

        desktopPanels.forEach((panel, i) => {
          panel.classList.toggle('is-active', i === safeIndex);
        });
      }
    }

    function getDesktopProgress() {
      if (!desktopTrack) return 0;

      const scrollableDistance = desktopTrack.offsetHeight - window.innerHeight;
      if (scrollableDistance <= 0) return 0;

      const trackTop = window.scrollY + desktopTrack.getBoundingClientRect().top;
      const currentScroll = window.scrollY - trackTop;

      return Math.max(0, Math.min(currentScroll / scrollableDistance, 1));
    }

    function applyMobileStack(stackPos) {
      const total = mobilePanels.length;
      if (!total) return;

      protectMobileTitle();

      const titleOffset = getMobileTitleOffset();

      mobilePanels.forEach((panel, i) => {
        const rel = i - stackPos;

        let translate = '0px';
        let scale = 1;
        let opacity = 1;

        if (rel >= 1) {
          translate = '110%';
          opacity = 0;
        } else if (rel > 0) {
          translate = rel * 110 + '%';
        } else {
          const depth = -rel;

          translate = -(depth * MOBILE_STACK_PEEK) + 'px';

          if (depth > 0) {
            scale = getLayerScale(panel, MOBILE_LAYER_GAP, depth, MOBILE_SCALE_MIN);
          }
        }

        panel.style.zIndex = String(i + 1);
        panel.style.opacity = String(opacity);

        /*
          Important:
          calc(titleOffset + translate)
          Isse mobile cards title ke niche rahenge.
        */
        panel.style.transform = 'translate3d(0, calc(' + titleOffset + 'px + ' + translate + '), 0) scale(' + scale + ')';
      });

      const safeIndex = Math.max(0, Math.min(Math.round(stackPos), total - 1));

      if (safeIndex !== mobileActiveIndex) {
        mobileActiveIndex = safeIndex;

        mobilePanels.forEach((panel, i) => {
          panel.classList.toggle('is-active', i === safeIndex);

          /*
            is-hidden remove rakha hai.
            Agar is-hidden display none karega to stack/peek visible nahi rahega.
          */
          panel.classList.remove('is-hidden');
        });

        const activePanel = mobilePanels[safeIndex];

        if (activePanel) {
          if (mobileCurrentNumber) {
            mobileCurrentNumber.textContent = activePanel.dataset.mvpNumber || '';
          }

          if (mobileCurrentTitle) {
            mobileCurrentTitle.textContent = activePanel.dataset.mvpTitle || '';
          }
        }
      }
    }

    function getMobileProgress() {
      if (!mobileTrack) return 0;

      // Extra scroll added to the track height (matches --mvp-mobile-lead).
      const leadPx = MOBILE_LEAD_RATIO * window.innerHeight;

      // Map only the scroll that remains after the lead hold, so stacking keeps
      // its full length while the first card lingers for the lead duration.
      const scrollableDistance = mobileTrack.offsetHeight - window.innerHeight - leadPx;
      if (scrollableDistance <= 0) return 0;

      const trackTop = window.scrollY + mobileTrack.getBoundingClientRect().top;
      const currentScroll = window.scrollY - trackTop - leadPx;

      return Math.max(0, Math.min(currentScroll / scrollableDistance, 1));
    }

    /*
      Convert the linear post-lead progress (0..1) into a stack position, adding
      a short dwell on each intermediate card so it lingers before the next one
      stacks on top. The first card's longer hold is the lead above; the last
      card gets no dwell.
    */
    function remapMobileStackPos(progress) {
      const total = mobilePanels.length;
      const transitions = total - 1;
      if (transitions < 1) return 0;

      const transitionWeight = 1;
      const holdWeight = MOBILE_REST_HOLD_RATIO;
      const intermediateHolds = transitions - 1; // cards 1 .. total-2
      const totalWeight = transitions * transitionWeight + intermediateHolds * holdWeight;

      let pos = Math.max(0, Math.min(progress, 1)) * totalWeight;

      for (let k = 0; k < transitions; k++) {
        // transition k: card k -> card k+1
        if (pos <= transitionWeight) {
          return k + pos / transitionWeight;
        }
        pos -= transitionWeight;

        // dwell on the card that just arrived, unless it's the last one
        if (k < transitions - 1) {
          if (pos <= holdWeight) {
            return k + 1;
          }
          pos -= holdWeight;
        }
      }

      return transitions;
    }

    function updateOnScroll() {
      ticking = false;

      if (getIsMobile()) {
        const total = mobilePanels.length;
        const stackPos = total > 1 ? remapMobileStackPos(getMobileProgress()) : 0;

        applyMobileStack(stackPos);
      } else {
        const total = desktopPanels.length;
        const stackPos = total > 1 ? getDesktopProgress() * (total - 1) : 0;

        applyDesktopStack(stackPos);
      }
    }

    function requestUpdate() {
      if (ticking) return;

      ticking = true;
      window.requestAnimationFrame(updateOnScroll);
    }

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const index = Number(tab.dataset.mvpTab);
        const total = desktopPanels.length;

        if (!desktopTrack || total <= 1) {
          applyDesktopStack(0);
          return;
        }

        const trackTop = window.scrollY + desktopTrack.getBoundingClientRect().top;
        const scrollableDistance = desktopTrack.offsetHeight - window.innerHeight;
        const targetScroll = trackTop + (scrollableDistance * index) / (total - 1);

        window.scrollTo({
          top: targetScroll,
          behavior: 'smooth'
        });
      });
    });

    preloadImages();
    protectMobileTitle();

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);

    applyDesktopStack(0);
    applyMobileStack(0);
    requestUpdate();
  }

  function initAllThreeMvpsSections(root = document) {
    const sections = root.querySelectorAll('[data-three-mvps-section]');
    sections.forEach(initThreeMvpsSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAllThreeMvpsSections();
    });
  } else {
    initAllThreeMvpsSections();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAllThreeMvpsSections(event.target);
  });
})();